import React, { useEffect, useState } from "react";
import { func, oneOf, string } from "prop-types";
import { Badge, Button, Image, Spinner, Stack } from "react-bootstrap";
import { uploadImage } from "@helpers";
import styles from "./styles/ImageUploader.module.css";
import {
  DEFAULT_IMAGE_SRC_169,
  DEFAULT_IMAGE_SRC_219,
  DEFAULT_IMAGE_SRC_43,
} from "@constants";
import clsx from "clsx";

const ImageUploader = ({ error, cropRatio, onUploadFinish, src }) => {
  // state
  const [image, setImage] = useState({
    name: "",
    src: "",
    type: "",
  });
  const [imageLoading, setImageLoading] = useState(false);
  // variables
  let spinner = null;
  let defaultSrc = DEFAULT_IMAGE_SRC_169;
  switch (cropRatio) {
    case 21 / 9:
      defaultSrc = DEFAULT_IMAGE_SRC_219;
      break;
    case 4 / 3:
      defaultSrc = DEFAULT_IMAGE_SRC_43;
      break;
    default:
      defaultSrc = DEFAULT_IMAGE_SRC_169;
      break;
  }

  // logics before hooks
  useEffect(() => {
    setImage({ ...image, src });
  }, [src]);
  // hooks

  // logics
  const upload = () => {
    const slug = `/webp${parseFloat(cropRatio).toFixed(2).toString()}`;
    uploadImage({
      cropShape: "rect",
      cropRatio,
      onUploaded: (files) => {
        if (files.length === 1) {
          setImageLoading(true);
          const { editedFile, originalFile } = files[0];
          const { name, type } = originalFile.file;
          const { fileUrl } = editedFile;
          setImage({ src: fileUrl + slug, name, type });
          onUploadFinish({ src: fileUrl + slug, name, type });
        }
      },
    });
  };

  // UI partials
  if (imageLoading) {
    spinner = (
      <div className={styles.spinner}>
        <Spinner animation="border" variant="danger" />
      </div>
    );
  }

  // effects

  // render
  return (
    <div
      className={clsx(
        "d-grid w-100 h-100 border rounded",
        error ? "border-danger" : ""
      )}
    >
      <Stack gap={3} className="align-items-center p-3 m-auto">
        <div className={styles.imageContainer}>
          <Image
            thumbnail
            src={image.src || defaultSrc}
            alt="image"
            onLoad={() => setImageLoading(false)}
          />
          {spinner}
        </div>
        {/* <span className="text-muted">{image.name || "No file selected"}</span> */}
        <div className="d-flex flex-column align-items-center">
          <Button variant="outline-primary" onClick={upload}>
            {image.src ? "CHANGE" : "UPLOAD"} IMAGE
          </Button>
          <div type="invalid" className="text-danger text-center">
            {error}
          </div>
        </div>
        <Stack className="align-items-center mt-2" gap={1}>
          <span>Accepted image's extension:</span>
          <div>
            <Stack direction="horizontal" gap={1} className="flex-wrap">
              <Badge bg="secondary">.jpg</Badge>
              <Badge bg="secondary">.jpeg</Badge>
              <Badge bg="secondary">.png</Badge>
              <Badge bg="secondary">.webp</Badge>
              <Badge bg="secondary">.svg</Badge>
            </Stack>
          </div>
        </Stack>
      </Stack>
    </div>
  );
};

/* required: display name */
ImageUploader.displayName = "ImageUploader";

/* required: props types */
ImageUploader.propTypes = {
  error: string,
  cropRatio: oneOf([21 / 9, 16 / 9, 4 / 3]),
  onUploadFinish: func,
};

/* required: default props */
ImageUploader.defaultProps = {
  error: "",
  cropRatio: 16 / 9,
  onUploadFinish: () => {},
};

export { ImageUploader };
